// src/services/user.ts
import api from "../lib/api";

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface ExpertProfile {
  offers?: string;
  content?: number[];
  tag?: string;
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  title?: string;
  organizationName?: string;
  organizationBrief?: string;
  segment?: string;
  country?: string;
  selectedSectors?: string[];
  experience?: string;
  userType: 'admin' | 'expert' | 'member';
  status: 'pending' | 'approved' | 'rejected';
  photoUrl?: string;
  companyLogoUrl?: string;
  expertProfile?: ExpertProfile;
  createdAt?: string;
  updatedAt?: string;
}

// ============================================
// ADMIN USER MANAGEMENT ENDPOINTS
// ============================================

/**
 * Get all users (Admin only)
 */
export async function getAllUsers(filters?: {
  status?: 'pending' | 'approved' | 'rejected';
  userType?: 'admin' | 'expert' | 'member';
}): Promise<User[]> {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.userType) params.append('userType', filters.userType);

  const { data } = await api.get(`/api/admin/users?${params.toString()}`);
  return data.data.users;
}

/**
 * Approve a user (Admin only)
 */
export async function approveUser(userId: string): Promise<User> {
  const { data } = await api.patch(`/api/admin/users/${userId}/approve`);
  return data.data.user;
}

/**
 * Reject a user (Admin only)
 */
export async function rejectUser(userId: string): Promise<User> {
  const { data } = await api.patch(`/api/admin/users/${userId}/reject`);
  return data.data.user;
}

/**
 * Change user role (Admin only)
 */
export async function changeUserRole(userId: string, userType: 'admin' | 'expert' | 'member'): Promise<User> {
  const { data } = await api.patch(`/api/admin/users/${userId}/role`, { userType });
  return data.data.user;
}

// ============================================
// PUBLIC MEMBER ENDPOINTS
// ============================================

/**
 * Get all approved members (Public - no auth required)
 */
export async function getMembers(filters?: {
  segment?: string;
  search?: string;
}): Promise<User[]> {
  const params = new URLSearchParams();
  if (filters?.segment) params.append('segment', filters.segment);
  if (filters?.search) params.append('search', filters.search);

  const { data } = await api.get(`/api/users/members?${params.toString()}`);
  return data.data.members;
}

/**
 * Get all approved experts (Public - no auth required)
 */
export async function getExperts(filters?: {
  segment?: string;
  search?: string;
  sectors?: string[];
}): Promise<User[]> {
  const params = new URLSearchParams();
  if (filters?.segment) params.append('segment', filters.segment);
  if (filters?.search) params.append('search', filters.search);
  if (filters?.sectors) {
    filters.sectors.forEach(sector => params.append('sectors', sector));
  }

  const { data } = await api.get(`/api/users/experts?${params.toString()}`);
  return data.data.experts;
}

/**
 * Get a single expert by ID (Public - no auth required)
 */
export async function getExpertById(id: string): Promise<User> {
  const { data } = await api.get(`/api/users/experts/${id}`);
  return data.data.expert;
}

/**
 * Get a single member by ID (Public - no auth required)
 */
export async function getMemberById(id: string): Promise<User> {
  const { data } = await api.get(`/api/users/members/${id}`);
  return data.data.member;
}

// ============================================
// USER PROFILE ENDPOINTS
// ============================================

/**
 * Get current user profile
 */
export async function getMyProfile(): Promise<User> {
  const { data } = await api.get('/api/users/me');
  return data.data.user;
}

/**
 * Update current user profile
 */
export async function updateMyProfile(updates: Partial<User>): Promise<User> {
  const { data } = await api.patch('/api/users/me', updates);
  return data.data.user;
}

/**
 * Change current user password
 */
export async function changeMyPassword(oldPassword: string, newPassword: string): Promise<void> {
  await api.patch('/api/users/me/password', { oldPassword, newPassword });
}
